import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState, useEffect } from "react";
import { z } from "zod";
import { ChevronLeft, ChevronRight, ChevronsUpDown, Loader2, Save, AlertCircle, Plus, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { useTenants } from "@/hooks/use-tenants";
import { CELLS, PRIMARY_CELL } from "@/lib/cells/cells";
import { primaryOnlyTenant } from "@/lib/cells/primary-only-tenant";
import { useBillingConfig, useUpdateBillingConfig, useBillingBreakdowns, useBillingTiers, useLedgerEntriesMulti, useCreateAdjustment } from "@/hooks/use-billing";
import type { BillingConfigFormData } from "@/hooks/use-billing";
import type { OverageInvoicing, BillingModel, BillingTierResponse, BillingBreakdownResponse, LedgerEntryResponse, LedgerPage } from "@harmonyso/billing-api-sdk";

const OVERAGE_INVOICING_OPTIONS: { value: OverageInvoicing; label: string }[] = [
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
  { value: "half_year", label: "Semi-Annual" },
  { value: "year", label: "Annually" },
];

const searchSchema = z.object({
  tenantId: z.string().optional(),
  tab: z.enum(["config", "usage", "ledger"]).optional(),
  months: z.string().optional(), // comma-separated YYYY-MM list
});

export const Route = createFileRoute("/billing")({
  component: BillingPage,
  validateSearch: zodValidator(searchSchema),
});

function centsToDisplay(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

const DEFAULT_LEDGER_PAGE_SIZE = 50;
const LEDGER_PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function dateToInputValue(d: Date | string | null | undefined): string {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  return date.toISOString().slice(0, 10); // YYYY-MM-DD
}

function dateToDisplay(d: Date | string | null | undefined): string {
  if (!d) return "—";
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString();
}


function BillingPage() {
  const { tenantId, tab, months: monthsParam } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [tenantPopoverOpen, setTenantPopoverOpen] = useState(false);

  const activeTab = tab ?? "config";
  const currentMonth = new Date().toISOString().slice(0, 7);
  const selectedMonths: string[] = monthsParam ? monthsParam.split(",") : [currentMonth];

  const { data: tenants, isLoading: tenantsLoading } = useTenants();
  const sortedTenants = useMemo(() => {
    if (!tenants) return [];
    return [...tenants].sort((a, b) => a.company.localeCompare(b.company));
  }, [tenants]);

  const selectedTenant = tenants?.find((t) => t.id === tenantId);
  // Billing only reaches the primary cell, so its tabs open only for a tenant
  // confirmed to be there.
  const billingTenant = primaryOnlyTenant({
    cellCount: CELLS.length,
    tenantId,
    primaryTenants: tenants,
    primaryTenantsLoading: tenantsLoading,
  });

  const updateSearch = (updates: Partial<{ tenantId: string | undefined; tab: "config" | "usage" | "ledger"; months: string | undefined }>) => {
    void navigate({ search: { tenantId, tab, months: monthsParam, ...updates } });
  };

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-hidden">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Billing</h1>
        <p className="text-muted-foreground">Manage tenant billing configuration and view usage breakdowns</p>
      </div>

      {/* Tenant selector */}
      <div className="flex items-center gap-3">
        <Popover open={tenantPopoverOpen} onOpenChange={setTenantPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={tenantPopoverOpen}
              className="w-72 justify-between"
              disabled={tenantsLoading}
            >
              {tenantsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : selectedTenant ? (
                <span className="truncate">{selectedTenant.company}</span>
              ) : (
                <span className="text-muted-foreground">Select tenant...</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0">
            <Command>
              <CommandInput placeholder="Search tenants..." />
              <CommandList>
                <CommandEmpty>No tenant found.</CommandEmpty>
                <CommandGroup>
                  {sortedTenants.map((t) => (
                    <CommandItem
                      key={t.id}
                      value={t.company}
                      onSelect={() => {
                        updateSearch({ tenantId: t.id });
                        setTenantPopoverOpen(false);
                      }}
                    >
                      <span className={cn(tenantId === t.id && "font-medium")}>{t.company}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {billingTenant.state === "loading" ? (
        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
      ) : billingTenant.state === "unavailable" ? (
        <p className="text-destructive text-sm">
          Couldn't load the {PRIMARY_CELL.residency} ({PRIMARY_CELL.id}) tenant
          list, so this tenant's billing can't be opened.
        </p>
      ) : billingTenant.state === "outside" ? (
        <p className="text-sm text-yellow-700 dark:text-yellow-300">
          Billing is only available for tenants in the {PRIMARY_CELL.residency} (
          {PRIMARY_CELL.id}) cell.
        </p>
      ) : (
        <BillingTabs
          tenantId={billingTenant.state === "ready" ? billingTenant.tenantId : undefined}
          activeTab={activeTab}
          selectedMonths={selectedMonths}
          onTabChange={(v) => { updateSearch({ tab: v }); }}
          onMonthsChange={(ms) => { updateSearch({ months: ms.join(",") }); }}
        />
      )}
    </div>
  );
}

function BillingTabs({
  tenantId,
  activeTab,
  selectedMonths,
  onTabChange,
  onMonthsChange,
}: {
  tenantId: string | undefined;
  activeTab: "config" | "usage" | "ledger";
  selectedMonths: string[];
  onTabChange: (tab: "config" | "usage" | "ledger") => void;
  onMonthsChange: (months: string[]) => void;
}) {
  const { data: config } = useBillingConfig(tenantId);
  const isFixed = config?.billing_model === "fixed";
  const hasNoConfig = tenantId && !config;

  useEffect(() => {
    if ((activeTab === "usage" || activeTab === "ledger") && (isFixed || hasNoConfig)) {
      onTabChange("config");
    }
  }, [isFixed, hasNoConfig, activeTab, onTabChange]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => { onTabChange(v as "config" | "usage" | "ledger"); }}
      className="flex flex-1 flex-col overflow-hidden"
    >
      <TabsList className="w-fit">
        <TabsTrigger value="config">Config</TabsTrigger>
        <TabsTrigger value="usage" disabled={!tenantId || isFixed || !!hasNoConfig}>Usage</TabsTrigger>
        <TabsTrigger value="ledger" disabled={!tenantId || !!hasNoConfig}>Ledger</TabsTrigger>
      </TabsList>

      {!tenantId ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-muted-foreground">Select a tenant to view billing information</p>
        </div>
      ) : (
        <>
          <TabsContent value="config" className="flex-1 overflow-y-auto">
            <BillingConfigTab tenantId={tenantId} />
          </TabsContent>

          <TabsContent value="usage" className="flex-1 overflow-y-auto">
            <BillingUsageTab tenantId={tenantId} selectedMonths={selectedMonths} onMonthsChange={onMonthsChange} />
          </TabsContent>

          <TabsContent value="ledger" className="flex-1 overflow-y-auto">
            <BillingLedgerTab tenantId={tenantId} selectedMonths={selectedMonths} onMonthsChange={onMonthsChange} />
          </TabsContent>
        </>
      )}
    </Tabs>
  );
}

function monthToDate(m: string): Date {
  return new Date(m + "-01T00:00:00");
}

function dateToMonth(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function MonthPicker({ selected, onChange }: { selected: string[]; onChange: (months: string[]) => void }) {
  const selectedDates = selected.map(monthToDate);

  const label = selected.length === 1
    ? format(monthToDate(selected[0]), "MMM yyyy")
    : `${selected.length} months`;

  return (
    <div className="flex items-center gap-2">
      <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
      <ReactDatePicker
        selected={selectedDates[0]}
        selectedDates={selectedDates}
        onChange={(dates) => {
          if (!dates || dates.length === 0) return;
          onChange(dates.map(dateToMonth).sort());
        }}
        selectsMultiple
        showMonthYearPicker
        shouldCloseOnSelect={false}
        dateFormat="MMM yyyy"
        customInput={
          <Button variant="outline" size="sm" className="w-36 justify-between">
            <span className="truncate">{label}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        }
      />
    </div>
  );
}

function BillingConfigTab({ tenantId }: { tenantId: string }) {
  const { data: config, isLoading, error } = useBillingConfig(tenantId);
  const { data: tiers, isLoading: tiersLoading } = useBillingTiers();
  const updateMutation = useUpdateBillingConfig(tenantId);

  const [form, setForm] = useState<BillingConfigFormData>({});
  const [isDirty, setIsDirty] = useState(false);

  // Reset form when tenant changes
  useEffect(() => {
    setForm({});
    setIsDirty(false);
  }, [tenantId]);

  // Sync form when config loads
  useEffect(() => {
    if (config) {
      setForm({
        tier_id: config.tier_id,
        billing_model: config.billing_model,
        fixed_amount_cents: config.fixed_amount_cents ?? undefined,
        free_requests_override: config.free_requests_override ?? undefined,
        credits_included_override: config.credits_included_override ?? undefined,
        price_per_credit_override: config.price_per_credit_override ?? undefined,
        minimum_commitment_override: config.minimum_commitment_override ?? undefined,
        overage_invoicing: config.overage_invoicing,
        contract_start_date: dateToInputValue(config.contract_start_date),
        contract_end_date: dateToInputValue(config.contract_end_date),
      });
      setIsDirty(false);
    }
  }, [config]);

  const handleChange = <K extends keyof BillingConfigFormData>(
    key: K,
    value: BillingConfigFormData[K],
  ) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Clear metered-only fields when switching to fixed
      if (key === "billing_model" && value === "fixed") {
        next.free_requests_override = undefined;
        next.credits_included_override = undefined;
        next.price_per_credit_override = undefined;
        next.minimum_commitment_override = undefined;
        next.overage_invoicing = undefined;
      }
      // Clear fixed-only field when switching to metered
      if (key === "billing_model" && value === "metered") {
        next.fixed_amount_cents = undefined;
      }
      return next;
    });
    setIsDirty(true);
  };

  const handleSubmit = async () => {
    try {
      const selectedTier = tiers?.find((t) => String(t.id) === form.tier_id);
      const payload = { ...form };

      // Don't send overrides that are identical to the tier default
      if (selectedTier) {
        if (payload.free_requests_override === selectedTier.free_requests_per_month) payload.free_requests_override = undefined;
        if (payload.credits_included_override === selectedTier.credits_included_per_month) payload.credits_included_override = undefined;
        if (payload.price_per_credit_override === selectedTier.price_per_credit_cents) payload.price_per_credit_override = undefined;
        if (payload.minimum_commitment_override === selectedTier.minimum_commitment_yearly_cents) payload.minimum_commitment_override = undefined;
      }

      await updateMutation.mutateAsync(payload);
      toast.success("Billing config saved");
      setIsDirty(false);
    } catch {
      toast.error("Failed to save billing config");
    }
  };

  if (isLoading || tiersLoading) {
    return (
      <div className="flex flex-col gap-4 pt-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !config) {
    const isNotFound = (error as { response?: { status?: number } } | null)?.response?.status === 404;
    return (
      <div className="flex flex-col gap-4 pt-4">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground" />
            <div>
              <p className="font-medium">
                {isNotFound ? "No billing config found for this tenant" : "Failed to load billing config"}
              </p>
              {isNotFound && (
                <p className="text-sm text-muted-foreground mt-1">
                  Fill in the form below to create one
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        <BillingConfigForm
          form={form}
          tiers={tiers ?? []}
          onChange={handleChange}
          onSubmit={() => void handleSubmit()}
          isSaving={updateMutation.isPending}
          isDirty={isDirty}
          isNew
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pt-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Current Config</CardTitle>
          <CardDescription>
            {config.billing_model === "metered" && (
              <>Tier: <span className="font-medium capitalize">{config.tier_name}</span> &nbsp;·&nbsp; </>
            )}
            Model: <span className="font-medium capitalize">{config.billing_model}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {config.billing_model === "fixed" ? (
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <div className="text-muted-foreground">Fixed Amount</div>
              <div className="font-medium">{config.fixed_amount_cents != null ? centsToDisplay(config.fixed_amount_cents) : "—"}</div>
              <div className="text-muted-foreground">Contract Period</div>
              <div className="font-medium">{dateToDisplay(config.contract_start_date)} → {dateToDisplay(config.contract_end_date)}</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm md:grid-cols-3">
              <div className="text-muted-foreground">Effective Free Requests</div>
              <div className="font-medium">{config.effective_free_requests.toLocaleString()}</div>
              <div />
              <div className="text-muted-foreground">Effective Credits Included</div>
              <div className="font-medium">{config.effective_credits_included.toLocaleString()}</div>
              <div />
              <div className="text-muted-foreground">Price per Credit</div>
              <div className="font-medium">{centsToDisplay(config.effective_price_per_credit_cents)}</div>
              <div />
              <div className="text-muted-foreground">Overage Invoicing</div>
              <div className="font-medium capitalize">{config.overage_invoicing.replace("_", " ")}</div>
              <div />
              <div className="text-muted-foreground">Contract Period</div>
              <div className="font-medium">{dateToDisplay(config.contract_start_date)} → {dateToDisplay(config.contract_end_date)}</div>
            </div>
          )}
        </CardContent>
      </Card>

      <BillingConfigForm
        form={form}
        tiers={tiers ?? []}
        onChange={handleChange}
        onSubmit={() => void handleSubmit()}
        isSaving={updateMutation.isPending}
        isDirty={isDirty}
      />
    </div>
  );
}

interface BillingConfigFormProps {
  form: BillingConfigFormData;
  tiers: BillingTierResponse[];
  onChange: <K extends keyof BillingConfigFormData>(key: K, value: BillingConfigFormData[K]) => void;
  onSubmit: () => void;
  isSaving: boolean;
  isDirty: boolean;
  isNew?: boolean;
}

function BillingConfigForm({ form, tiers, onChange, onSubmit, isSaving, isDirty, isNew }: BillingConfigFormProps) {
  const isFixed = form.billing_model === "fixed";
  const selectedTier = tiers.find((t) => String(t.id) === form.tier_id);

  const isSubmitDisabled = isSaving || (!isDirty && !isNew) || (
    !isFixed && (
      !form.overage_invoicing ||
      !form.contract_start_date ||
      !form.contract_end_date
    )
  ) || (
    isFixed && (
      !form.contract_start_date ||
      !form.contract_end_date
    )
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{isNew ? "Create Billing Config" : "Edit Config"}</CardTitle>
        {isNew && (
          <CardDescription>Set up billing configuration for this tenant</CardDescription>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {/* Billing Model — always shown; Tier only for metered */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label>Billing Model</Label>
            <Select
              value={form.billing_model ?? ""}
              onValueChange={(v) => { onChange("billing_model", v as BillingModel); }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select model..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="metered">Metered</SelectItem>
                <SelectItem value="fixed">Fixed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.billing_model === "metered" && (
            <div className="flex flex-col gap-1.5">
              <Label>Tier</Label>
              <Select
                value={form.tier_id ?? ""}
                onValueChange={(v) => { onChange("tier_id", v); }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tier..." />
                </SelectTrigger>
                <SelectContent>
                  {[...tiers].sort((a, b) => a.minimum_commitment_yearly_cents - b.minimum_commitment_yearly_cents).map((t) => (
                    <SelectItem key={String(t.id)} value={String(t.id)}>
                      {t.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Fixed: amount + contract dates only */}
        {isFixed && (
          <>
            <div className="flex flex-col gap-1.5">
              <Label>Fixed Amount (cents)</Label>
              <Input
                type="number"
                placeholder="e.g. 100000 for $1,000"
                value={form.fixed_amount_cents ?? ""}
                onChange={(e) =>
                  { onChange("fixed_amount_cents", e.target.value ? parseInt(e.target.value) : undefined); }
                }
              />
              {form.fixed_amount_cents != null && (
                <p className="text-xs text-muted-foreground">= {centsToDisplay(form.fixed_amount_cents)}</p>
              )}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Contract Start Date</Label>
                <Input
                  type="date"
                  value={form.contract_start_date ?? ""}
                  onChange={(e) => { onChange("contract_start_date", e.target.value || undefined); }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Contract End Date</Label>
                <Input
                  type="date"
                  value={form.contract_end_date ?? ""}
                  onChange={(e) => { onChange("contract_end_date", e.target.value || undefined); }}
                />
              </div>
            </div>
          </>
        )}

        {/* Metered: fields + overage invoicing + contract dates */}
        {!isFixed && form.billing_model && (
          <>
            <Separator />
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <Label>Free Requests / Month</Label>
                <Input
                  type="number"
                  placeholder={selectedTier ? String(selectedTier.free_requests_per_month) : "—"}
                  value={form.free_requests_override ?? ""}
                  onChange={(e) =>
                    { onChange("free_requests_override", e.target.value ? parseInt(e.target.value) : undefined); }
                  }
                />
                {form.free_requests_override == null && selectedTier && (
                  <p className="text-xs text-muted-foreground">Tier default: {selectedTier.free_requests_per_month.toLocaleString()}</p>
                )}
                {form.free_requests_override != null && (
                  <p className="text-xs text-muted-foreground">Overriding tier default ({selectedTier?.free_requests_per_month.toLocaleString() ?? "—"})</p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Credits Included / Month</Label>
                <Input
                  type="number"
                  placeholder={selectedTier ? String(selectedTier.credits_included_per_month) : "—"}
                  value={form.credits_included_override ?? ""}
                  onChange={(e) =>
                    { onChange("credits_included_override", e.target.value ? parseInt(e.target.value) : undefined); }
                  }
                />
                {form.credits_included_override == null && selectedTier && (
                  <p className="text-xs text-muted-foreground">Tier default: {selectedTier.credits_included_per_month.toLocaleString()}</p>
                )}
                {form.credits_included_override != null && (
                  <p className="text-xs text-muted-foreground">Overriding tier default ({selectedTier?.credits_included_per_month.toLocaleString() ?? "—"})</p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Price per Credit (cents)</Label>
                <Input
                  type="number"
                  placeholder={selectedTier ? String(selectedTier.price_per_credit_cents) : "—"}
                  value={form.price_per_credit_override ?? ""}
                  onChange={(e) =>
                    { onChange("price_per_credit_override", e.target.value ? parseInt(e.target.value) : undefined); }
                  }
                />
                {form.price_per_credit_override == null && selectedTier && (
                  <p className="text-xs text-muted-foreground">Tier default: {centsToDisplay(selectedTier.price_per_credit_cents)}</p>
                )}
                {form.price_per_credit_override != null && (
                  <p className="text-xs text-muted-foreground">Overriding tier default ({selectedTier ? centsToDisplay(selectedTier.price_per_credit_cents) : "—"})</p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Min. Commitment (cents/year)</Label>
                <Input
                  type="number"
                  placeholder={selectedTier ? String(selectedTier.minimum_commitment_yearly_cents) : "—"}
                  value={form.minimum_commitment_override ?? ""}
                  onChange={(e) =>
                    { onChange("minimum_commitment_override", e.target.value ? parseInt(e.target.value) : undefined); }
                  }
                />
                {form.minimum_commitment_override == null && selectedTier && (
                  <p className="text-xs text-muted-foreground">Tier default: {centsToDisplay(selectedTier.minimum_commitment_yearly_cents)}</p>
                )}
                {form.minimum_commitment_override != null && (
                  <p className="text-xs text-muted-foreground">Overriding tier default ({selectedTier ? centsToDisplay(selectedTier.minimum_commitment_yearly_cents) : "—"})</p>
                )}
              </div>
            </div>

            <Separator />

            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <Label>Overage Invoicing</Label>
                <Select
                  value={form.overage_invoicing ?? ""}
                  onValueChange={(v) => { onChange("overage_invoicing", v as OverageInvoicing); }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {OVERAGE_INVOICING_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Contract Start Date</Label>
                <Input
                  type="date"
                  value={form.contract_start_date ?? ""}
                  onChange={(e) => { onChange("contract_start_date", e.target.value || undefined); }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Contract End Date</Label>
                <Input
                  type="date"
                  value={form.contract_end_date ?? ""}
                  onChange={(e) => { onChange("contract_end_date", e.target.value || undefined); }}
                />
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end">
          <Button onClick={onSubmit} disabled={isSubmitDisabled} className="min-w-24">
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isNew ? "Create" : "Save"}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


interface CombinedBreakdown {
  billing_model: BillingBreakdownResponse["billing_model"];
  months: number;
  total_requests: number;
  free_requests_limit: number;
  free_requests_used: number;
  new_request_credits: number;
  resolved_by_ai_count: number;
  resolved_by_ai_credits: number;
  agent_action_count: number;
  agent_action_credits: number;
  total_credits_consumed: number;
  credits_included: number;
  overage_credits: number;
  overage_amount_cents: number;
  price_per_credit_cents: number;
  fixed_amount_cents: number;
}

function sumBreakdowns(breakdowns: BillingBreakdownResponse[]): CombinedBreakdown {
  const base = breakdowns[0];
  return {
    billing_model: base.billing_model,
    months: breakdowns.length,
    total_requests: breakdowns.reduce((s, b) => s + (b.total_requests ?? 0), 0),
    free_requests_limit: breakdowns.reduce((s, b) => s + (b.free_requests_limit ?? 0), 0),
    free_requests_used: breakdowns.reduce((s, b) => s + (b.free_requests_used ?? 0), 0),
    new_request_credits: breakdowns.reduce((s, b) => s + (b.new_request_credits ?? 0), 0),
    resolved_by_ai_count: breakdowns.reduce((s, b) => s + (b.resolved_by_ai_count ?? 0), 0),
    resolved_by_ai_credits: breakdowns.reduce((s, b) => s + (b.resolved_by_ai_credits ?? 0), 0),
    agent_action_count: breakdowns.reduce((s, b) => s + (b.agent_action_count ?? 0), 0),
    agent_action_credits: breakdowns.reduce((s, b) => s + (b.agent_action_credits ?? 0), 0),
    total_credits_consumed: breakdowns.reduce((s, b) => s + (b.total_credits_consumed ?? 0), 0),
    credits_included: breakdowns.reduce((s, b) => s + (b.credits_included ?? 0), 0),
    overage_credits: breakdowns.reduce((s, b) => s + (b.overage_credits ?? 0), 0),
    overage_amount_cents: breakdowns.reduce((s, b) => s + (b.overage_amount_cents ?? 0), 0),
    price_per_credit_cents: base.price_per_credit_cents ?? 0,
    fixed_amount_cents: breakdowns.reduce((s, b) => s + (b.fixed_amount_cents ?? 0), 0),
  };
}

function BillingUsageTab({
  tenantId,
  selectedMonths,
  onMonthsChange,
}: {
  tenantId: string;
  selectedMonths: string[];
  onMonthsChange: (months: string[]) => void;
}) {
  const results = useBillingBreakdowns(tenantId, selectedMonths);
  const { data: config } = useBillingConfig(tenantId);
  const { data: tiers } = useBillingTiers();
  const selectedTier = tiers?.find((t) => String(t.id) === config?.tier_id);
  const monthlyCommitment = selectedTier ? Math.round(selectedTier.minimum_commitment_yearly_cents / 12) : null;

  const isLoading = results.some((r) => r.isLoading);
  const loaded = results.map((r) => r.data).filter((d): d is BillingBreakdownResponse => d !== undefined);
  const combined = loaded.length > 0 ? sumBreakdowns(loaded) : null;
  const multiMonth = selectedMonths.length > 1;

  return (
    <div className="flex flex-col gap-4 pt-4">
      <div className="flex items-center gap-3">
        <Label className="shrink-0">Billing Months</Label>
        <MonthPicker selected={selectedMonths} onChange={onMonthsChange} />
        {multiMonth && (
          <span className="text-sm text-muted-foreground">Showing aggregated totals for {selectedMonths.length} months</span>
        )}
      </div>

      {isLoading && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {!isLoading && !combined && (
        <Card>
          <CardContent className="flex items-center gap-3 py-8 text-center justify-center">
            <AlertCircle className="h-6 w-6 text-muted-foreground" />
            <p className="text-muted-foreground">No billing data found for the selected period</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && combined && (
        <>
          {combined.billing_model === "fixed" ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Fixed Billing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">
                  {centsToDisplay(combined.fixed_amount_cents)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {multiMonth ? `Total across ${selectedMonths.length} months` : "Flat monthly fee"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Metric</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">Credits</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>Total Requests</TableCell>
                        <TableCell className="text-right font-medium">{combined.total_requests.toLocaleString()}</TableCell>
                        <TableCell className="text-right">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6 text-muted-foreground">Free Requests Used</TableCell>
                        <TableCell className="text-right">{combined.free_requests_used.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          (limit: {combined.free_requests_limit.toLocaleString()})
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>New Request Credits</TableCell>
                        <TableCell className="text-right">{(combined.total_requests - combined.free_requests_used).toLocaleString()}</TableCell>
                        <TableCell className="text-right font-medium">{combined.new_request_credits.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>AI Resolutions</TableCell>
                        <TableCell className="text-right">{combined.resolved_by_ai_count.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-medium">{combined.resolved_by_ai_credits.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Agent Actions</TableCell>
                        <TableCell className="text-right">{combined.agent_action_count.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-medium">{combined.agent_action_credits.toLocaleString()}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Credits & Overage</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Credits</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>Total Credits Consumed</TableCell>
                        <TableCell className="text-right font-medium">{combined.total_credits_consumed.toLocaleString()}</TableCell>
                        <TableCell className="text-right">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6 text-muted-foreground">Credits Included</TableCell>
                        <TableCell className="text-right">{combined.credits_included.toLocaleString()}</TableCell>
                        <TableCell className="text-right">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6 text-muted-foreground">Overage Credits</TableCell>
                        <TableCell className="text-right">{combined.overage_credits.toLocaleString()}</TableCell>
                        <TableCell className="text-right">—</TableCell>
                      </TableRow>
                      <TableRow className="font-semibold">
                        <TableCell>Price per Credit</TableCell>
                        <TableCell className="text-right">—</TableCell>
                        <TableCell className="text-right">{centsToDisplay(combined.price_per_credit_cents)}</TableCell>
                      </TableRow>
                      <TableRow className="font-semibold bg-muted/40">
                        <TableCell>Overage Amount</TableCell>
                        <TableCell className="text-right">{combined.overage_credits.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-orange-600 dark:text-orange-400">
                          {centsToDisplay(combined.overage_amount_cents)}
                        </TableCell>
                      </TableRow>
                      {monthlyCommitment != null && (
                        <TableRow className="font-semibold">
                          <TableCell>Total Due (commitment × {selectedMonths.length} + overage)</TableCell>
                          <TableCell className="text-right">—</TableCell>
                          <TableCell className="text-right">
                            {centsToDisplay(monthlyCommitment * selectedMonths.length + combined.overage_amount_cents)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}

function BillingLedgerTab({
  tenantId,
  selectedMonths,
  onMonthsChange,
}: {
  tenantId: string;
  selectedMonths: string[];
  onMonthsChange: (months: string[]) => void;
}) {
  // Adjustment always targets the most recent selected month
  const adjMonth = [...selectedMonths].sort().at(-1) ?? selectedMonths[0];

  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [adjForm, setAdjForm] = useState<{ entry_type: "debit" | "credit"; amount: string; description: string }>({
    entry_type: "credit",
    amount: "",
    description: "",
  });
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerPageSize, setLedgerPageSize] = useState(DEFAULT_LEDGER_PAGE_SIZE);
  const selectedMonthsKey = selectedMonths.join(",");

  useEffect(() => {
    setLedgerPage(1);
  }, [tenantId, selectedMonthsKey]);

  const results = useLedgerEntriesMulti(tenantId, selectedMonths, ledgerPage, ledgerPageSize);
  const createAdjustment = useCreateAdjustment(tenantId);

  const isLoading = results.some((r) => r.isLoading);
  const ledgerPages = useMemo((): (LedgerPage & { month: string })[] => {
    return results.flatMap((r) => {
      if (!r.data) return [];
      const { month, data } = r.data as { month: string; data: LedgerPage };
      return [{ ...data, month }];
    });
  }, [results]);

  const allItems = useMemo((): (LedgerEntryResponse & { month: string })[] => {
    return ledgerPages
      .flatMap((page) => page.items.map((e) => ({ ...e, month: page.month })))
      .sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
  }, [ledgerPages]);

  const totalEntries = ledgerPages.reduce((sum, page) => sum + page.total, 0);
  const totalPages = Math.max(1, ...ledgerPages.map((page) => page.pages));
  const canPreviousPage = ledgerPage > 1;
  const canNextPage = ledgerPage < totalPages;

  const handleAdjustmentSubmit = async () => {
    const amount = parseInt(adjForm.amount);
    if (!amount || !adjForm.description) return;
    try {
      await createAdjustment.mutateAsync({
        entry_type: adjForm.entry_type,
        amount,
        description: adjForm.description,
        month: adjMonth,
      });
      toast.success("Adjustment created");
      setShowAdjustmentForm(false);
      setAdjForm({ entry_type: "credit", amount: "", description: "" });
    } catch {
      toast.error("Failed to create adjustment");
    }
  };

  const multiMonth = selectedMonths.length > 1;

  return (
    <div className="flex flex-col gap-4 pt-4">
      {/* Controls row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Label className="shrink-0">Billing Months</Label>
          <MonthPicker selected={selectedMonths} onChange={onMonthsChange} />
        </div>
        <Button size="sm" onClick={() => { setShowAdjustmentForm((v) => !v); }}>
          <Plus className="mr-2 h-4 w-4" />
          Add Adjustment
        </Button>
      </div>

      {/* Adjustment form */}
      {showAdjustmentForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              New Adjustment
              {multiMonth && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  → {format(new Date(adjMonth + "-01"), "MMM yyyy")}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <Label>Type</Label>
                <Select
                  value={adjForm.entry_type}
                  onValueChange={(v) => { setAdjForm((f) => ({ ...f, entry_type: v as "debit" | "credit" })); }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">Credit (add credits)</SelectItem>
                    <SelectItem value="debit">Debit (remove credits)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Amount (credits)</Label>
                <Input
                  type="number"
                  placeholder="e.g. 500"
                  value={adjForm.amount}
                  onChange={(e) => { setAdjForm((f) => ({ ...f, amount: e.target.value })); }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Description</Label>
                <Input
                  placeholder="Reason for adjustment"
                  value={adjForm.description}
                  onChange={(e) => { setAdjForm((f) => ({ ...f, description: e.target.value })); }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setShowAdjustmentForm(false); }}>Cancel</Button>
              <Button
                size="sm"
                disabled={!adjForm.amount || !adjForm.description || createAdjustment.isPending}
                onClick={() => void handleAdjustmentSubmit()}
              >
                {createAdjustment.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading && <Skeleton className="h-64 w-full" />}

      {!isLoading && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Ledger Entries
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {totalEntries > allItems.length ? `${allItems.length} shown of ${totalEntries}` : `${totalEntries} total`}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  {multiMonth && <TableHead>Month</TableHead>}
                  <TableHead>Type</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={multiMonth ? 6 : 5} className="py-8 text-center text-muted-foreground">
                      No entries for this period
                    </TableCell>
                  </TableRow>
                ) : (
                  allItems.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {entry.created_at.toLocaleString()}
                      </TableCell>
                      {multiMonth && (
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {format(new Date(entry.month + "-01"), "MMM yyyy")}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant={entry.entry_type === "credit" ? "default" : "secondary"} className="capitalize">
                          {entry.entry_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm capitalize">{entry.event_type.replace(/_/g, " ")}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{entry.description ?? "—"}</TableCell>
                      <TableCell className={cn("text-right font-medium tabular-nums", entry.entry_type === "debit" ? "text-orange-600 dark:text-orange-400" : "text-green-600 dark:text-green-400")}>
                        {entry.entry_type === "debit" ? "-" : "+"}{entry.amount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {totalEntries > 0 && (
              <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                  Page {ledgerPage} of {totalPages}
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">Rows per page</span>
                    <Select
                      value={ledgerPageSize.toString()}
                      onValueChange={(value) => {
                        setLedgerPageSize(Number(value));
                        setLedgerPage(1);
                      }}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="h-8 w-[75px] justify-center" size="sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent side="top">
                        {LEDGER_PAGE_SIZE_OPTIONS.map((pageSize) => (
                          <SelectItem key={pageSize} value={pageSize.toString()}>
                            {pageSize}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!canPreviousPage}
                      onClick={() => { setLedgerPage((page) => Math.max(1, page - 1)); }}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!canNextPage}
                      onClick={() => { setLedgerPage((page) => Math.min(totalPages, page + 1)); }}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
